import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-modal";
import ReactQuill from "react-quill-new";
import DOMPurify from "dompurify";
import api from "../utils/axiosInstance";
import "react-quill-new/dist/quill.snow.css";
import "../styles/NewsModal.css";

Modal.setAppElement("#root");

const MAX_TEXT_CHARS = 5000;

const quillModules = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["blockquote", "code-block"],
        ["link"],
        ["clean"],
    ],
};
const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "blockquote",
    "code-block",
    "link",
];

const pad = (n) => String(n).padStart(2, "0");
const getLocalMinute = (d = new Date()) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const getTodayDate = () => {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00`;
};
const formatDateDisplay = (d = new Date()) =>
    `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
const floorNowToMinute = () => {
    const d = new Date();
    d.setSeconds(0, 0);
    return d;
};
const toUtcISOStringFromLocalMinute = (localMinuteStr) => {
    if (!localMinuteStr) return "";
    const d = new Date(localMinuteStr);
    d.setSeconds(0, 0);
    return d.toISOString();
};

const NewsModal = ({ news, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        title: "",
        preview: "",
        text: "",
        publishDate: null,
        readyToPublish: false,
        priority: false,
        image: null,
    });
    const [currentImageUrl, setCurrentImageUrl] = useState("");
    const [error, setError] = useState("");
    const [publishDateError, setPublishDateError] = useState("");
    const [imageError, setImageError] = useState("");
    const [textLength, setTextLength] = useState(0);
    const [imagePreviewUrl, setImagePreviewUrl] = useState("");
    const [initialLockedData, setInitialLockedData] = useState(null);

    useEffect(() => {
        if (news) {
            const text = news.text || "";

            const localDate = news.publishDate
                ? getLocalMinute(new Date(news.publishDate))
                : null;

            setFormData({
                title: news.title || "",
                preview: news.preview || "",
                text,
                publishDate: news.readyToPublish ? localDate : null,
                readyToPublish: news.readyToPublish ?? false,
                priority: !!news.priority,
                image: null,
            });

            setCurrentImageUrl(news.image || "");
            const div = document.createElement("div");
            div.innerHTML = text;
            setTextLength((div.textContent || "").trim().length);
            setImageError("");
            setError("");
            setPublishDateError("");
            setImagePreviewUrl("");
            // Save initial values of editable-when-locked fields
            if (news.published) {
                setInitialLockedData({
                    readyToPublish: news.readyToPublish ?? false,
                });
            } else {
                setInitialLockedData(null);
            }
        } else {
            setFormData({
                title: "",
                preview: "",
                text: "",
                publishDate: null,
                readyToPublish: false,
                priority: false,
                image: null,
            });
            setCurrentImageUrl("");
            setTextLength(0);
            setImageError("");
            setError("");
            setPublishDateError("");
            setImagePreviewUrl("");
        }
    }, [news]);

    const handleQuillChange = (content, _delta, _source, editor) => {
        const plain = editor.getText().replace(/\n$/, "");
        const len = plain.length;
        if (len > MAX_TEXT_CHARS) setError(`Text is too long: ${len}/${MAX_TEXT_CHARS} characters.`);
        else if (error && error.startsWith("Text is too long")) setError("");
        setTextLength(len);
        setFormData((prev) => ({ ...prev, text: content }));
    };

    const cleanHtml = useMemo(
        () =>
            DOMPurify.sanitize(formData.text, {
                ALLOWED_TAGS: [
                    "p",
                    "strong",
                    "em",
                    "u",
                    "s",
                    "ul",
                    "ol",
                    "li",
                    "br",
                    "h1",
                    "h2",
                    "h3",
                    "blockquote",
                    "code",
                    "pre",
                    "a",
                ],
                ALLOWED_ATTR: ["href", "target", "rel"],
            }),
        [formData.text]
    );

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isLocked && formData.publishDate) {
            const selected = new Date(formData.publishDate);
            selected.setSeconds(0, 0);
            const nowFloored = floorNowToMinute();
            if (selected < nowFloored) {
                setPublishDateError(`Publish Date cannot be earlier than the current time (${formatDateDisplay(nowFloored)}).`);
                return;
            }
        }
        setPublishDateError("");

        if (imageError) {
            setError("Please fix image validation error.");
            return;
        }
        if (textLength > MAX_TEXT_CHARS) {
            setError(`Text must be ≤ ${MAX_TEXT_CHARS} characters (now ${textLength}).`);
            return;
        }
        if (!formData.title.trim() || !formData.preview.trim()) {
            setError("Title and Preview are required.");
            return;
        }

        const utcDate =
            formData.publishDate !== null && formData.publishDate !== ""
                ? toUtcISOStringFromLocalMinute(formData.publishDate)
                : "";

        const form = new FormData();
        form.append("title", formData.title);
        form.append("preview", formData.preview);
        form.append("text", cleanHtml);

        if (utcDate) form.append("publish_date", utcDate);
        form.append("ready_to_publish", formData.readyToPublish ? "1" : "0");
        form.append("priority", formData.priority ? "1" : "0");
        if (formData.image) form.append("image", formData.image);

        const url = news?.id ? `/news/${news.id}/update` : "/news/add";

        try {
            const response = await api.post(url, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onSave(response.data);
        } catch (err) {
            const msg =
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                err?.message ||
                "Failed to save news";
            setError(msg);
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) {
            setFormData((prev) => ({ ...prev, image: null }));
            setImageError("");
            revokePreviewUrl();
            return;
        }
        const allowedTypes = ["image/png", "image/jpeg", "image/webp"];
        if (!allowedTypes.includes(file.type)) {
            setImageError("Invalid file format. Only PNG, JPEG, or WEBP allowed.");
            setFormData((prev) => ({ ...prev, image: null }));
            revokePreviewUrl();
            return;
        }
        if (file.size > 100 * 1024 * 1024) {
            setImageError("File size must not exceed 100 MB.");
            setFormData((prev) => ({ ...prev, image: null }));
            revokePreviewUrl();
            return;
        }
        const objectUrl = URL.createObjectURL(file);
        const img = new Image();
        img.src = objectUrl;
        img.onload = () => {
            const validWidth = img.width >= 854 && img.width <= 940;
            const validHeight = img.height >= 480 && img.height <= 530;
            if (!validWidth || !validHeight) {
                setImageError("Image resolution must be from 854x480 to 940x530.");
                setFormData((prev) => ({ ...prev, image: null }));
                URL.revokeObjectURL(objectUrl);
                setImagePreviewUrl("");
                return;
            }
            setImageError("");
            setFormData((prev) => ({ ...prev, image: file }));
            setImagePreviewUrl(objectUrl);
        };
        img.onerror = () => {
            setImageError("Could not load the selected image.");
            setFormData((prev) => ({ ...prev, image: null }));
            URL.revokeObjectURL(objectUrl);
            setImagePreviewUrl("");
        };
    };

    const revokePreviewUrl = () => {
        if (imagePreviewUrl) {
            URL.revokeObjectURL(imagePreviewUrl);
            setImagePreviewUrl("");
        }
    };
    useEffect(() => () => revokePreviewUrl(), []);

    // Lock all fields except readyToPublish when the news is published
    const isLocked = !!(news?.published);

    // Check if locked fields have changed from their initial values
    const hasLockedFieldsChanged = isLocked && initialLockedData !== null && (
        formData.readyToPublish !== initialLockedData.readyToPublish
    );

    const isSubmitDisabled = isLocked
        ? !hasLockedFieldsChanged
        : (!!imageError || textLength > MAX_TEXT_CHARS || !formData.title.trim() || !formData.preview.trim());

    return (
        <Modal isOpen={true} onRequestClose={onClose} className="modal-content" overlayClassName="modal-overlay">
            <div className="modal-header">
                <h2>{news ? "Edit News" : "Add News"}</h2>
                <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
                    ×
                </button>
            </div>

            <form className="modal-form" onSubmit={handleSubmit}>
                <label htmlFor="news-title">Title*</label>
                <input
                    id="news-title"
                    type="text"
                    maxLength={255}
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    disabled={isLocked}
                    className={isLocked ? "disabled-input" : ""}
                />

                <label htmlFor="news-preview">Preview*</label>
                <textarea
                    id="news-preview"
                    maxLength={300}
                    value={formData.preview}
                    onChange={(e) => setFormData({ ...formData, preview: e.target.value })}
                    required
                    disabled={isLocked}
                    className={isLocked ? "disabled-input" : ""}
                />

                <label>Text*</label>
                <div className={`wysiwyg-wrapper${isLocked ? " wysiwyg-locked" : ""}`}>
                    <ReactQuill
                        theme="snow"
                        value={formData.text}
                        onChange={handleQuillChange}
                        modules={quillModules}
                        formats={quillFormats}
                        readOnly={isLocked}
                        bounds=".wysiwyg-wrapper"
                    />
                    <div className={`field-description ${textLength > MAX_TEXT_CHARS ? "error" : ""}`}>
                        {textLength}/{MAX_TEXT_CHARS} characters (plain text).
                    </div>
                </div>

                <label>Image</label>
                {currentImageUrl && !imagePreviewUrl && (
                    <div className="current-image">
                        <img src={currentImageUrl} alt="Current" className="news-thumb" width="120" height="68" />
                    </div>
                )}
                {imagePreviewUrl && (
                    <div className="current-image">
                        <img src={imagePreviewUrl} alt="Selected" className="news-thumb" width="120" height="68" />
                    </div>
                )}
                <input
                    type="file"
                    accept=".png,.jpeg,.jpg,.webp"
                    onChange={handleImageChange}
                    disabled={isLocked}
                    className={isLocked ? "disabled-input" : ""}
                />
                {imageError && <div className="modal-error">{imageError}</div>}

                {isLocked && (
                    <div className="modal-locked-notice">
                        ⚠️ Only <strong>Ready to Publish</strong> can be changed.
                    </div>
                )}

                <div className="form-group">
                    <span>Ready to Publish</span>
                    <div className="switch">
                        <input
                            id="ready-switch"
                            type="checkbox"
                            checked={formData.readyToPublish === true}
                            onChange={(e) => {
                                const checked = e.target.checked;
                                setFormData((prev) => ({
                                    ...prev,
                                    readyToPublish: checked,
                                    publishDate: checked ? prev.publishDate : null,
                                }));
                            }}
                        />
                        <label htmlFor="ready-switch" className="slider" />
                    </div>
                </div>

                <label htmlFor="publish-date">Publish Date (optional)</label>
                <input
                    id="publish-date"
                    type="datetime-local"
                    value={formData.publishDate || ""}
                    onChange={(e) => {
                        setFormData({ ...formData, publishDate: e.target.value });
                        setPublishDateError("");
                    }}
                    disabled={isLocked || !formData.readyToPublish}
                    className={isLocked || !formData.readyToPublish ? "disabled-input" : ""}
                    placeholder="Select publish date"
                    min={getTodayDate()}
                />
                {publishDateError && <div className="modal-error">{publishDateError}</div>}

                <div className="form-group">
                    <span>Priority</span>
                    <div className="switch">
                        <input
                            id="priority-switch"
                            type="checkbox"
                            checked={formData.priority}
                            onChange={(e) => setFormData({ ...formData, priority: e.target.checked })}
                            disabled={isLocked}
                        />
                        <label htmlFor="priority-switch" className={`slider${isLocked ? " slider-disabled" : ""}`} />
                    </div>
                </div>

                {error && <div className="modal-error">{error}</div>}

                <button type="submit" className="modal-button" disabled={isSubmitDisabled}>
                    {news ? "Save Changes" : "Create"}
                </button>
            </form>
        </Modal>
    );
};

export default NewsModal;
